import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Theme } from '@jotpsych/ui/mui';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import ScheduleIcon from '@mui/icons-material/ScheduleOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import { Box, Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';

import { AddNoteComposer } from './AddNoteComposer';
import { ArchiveDialog, type ArchiveSubject } from './ArchiveDialog';
import { stopPortalEvents } from './portalGuard';
import { SnoozeMenu } from './SnoozeMenu';

/** 0.8125rem on 32px rows, the size the queue rows set. */
const ITEM_SX = { fontSize: '0.8125rem', minHeight: 32, py: 0 } as const;
const ICON_SX = { minWidth: 30 } as const;

export const WORK_MENU_PAPER_SX = (theme: Theme) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 1.5,
  boxShadow: '0 10px 30px rgba(15,23,42,0.14)',
  minWidth: 220,
});

/**
 * One item's worth of vocabulary, so every surface spells them the same.
 * Exported for the menus that share the surface without sharing the verbs —
 * the per-note menu in the workbench Notes card is one.
 */
export function WorkMenuItem({
  icon,
  label,
  onSelect,
  disabled,
  destructive,
  trailing,
  itemRef,
}: {
  icon: ReactNode;
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  destructive?: boolean;
  trailing?: ReactNode;
  itemRef?: React.Ref<HTMLLIElement>;
}) {
  return (
    <MenuItem
      disabled={disabled}
      onClick={onSelect}
      ref={itemRef}
      sx={destructive ? { ...ITEM_SX, color: 'error.main' } : ITEM_SX}
    >
      <ListItemIcon sx={destructive ? { ...ICON_SX, color: 'error.main' } : ICON_SX}>
        {icon}
      </ListItemIcon>
      <ListItemText slotProps={{ primary: { sx: { fontSize: '0.8125rem' } } }}>
        {label}
      </ListItemText>
      {trailing}
    </MenuItem>
  );
}

export interface WorkMenuDestructiveAction {
  label: string;
  onSelect: () => void;
  busy?: boolean;
}

export interface WorkMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /**
   * Names the encounter in the archive dialog ("Hides Ana Cohen · 12 Aug ·
   * Aetna and every claim…"). Archive… is offered only when this and
   * `onArchive` are both present: a dialog with a blank in that sentence is
   * worse than no item.
   */
  subject?: ArchiveSubject;
  /** The encounter is archived or snoozed: Restore replaces the two hiding verbs. */
  hidden?: boolean;
  busy?: boolean;
  /** Frozen clock for stories and tests. */
  today?: Date;
  /**
   * Each verb appears only when its handler does. A caller with nothing but
   * `destructive` gets a one-item menu on this same surface, not a second
   * menu that has to be kept looking like it.
   */
  onArchive?: (opts: { note?: string }) => void;
  /** A surface with several archive triggers can own the shared dialog. */
  onRequestArchive?: () => void;
  onSnooze?: (opts: { untilDate: string }) => void;
  onAddNote?: (opts: { body: string }) => void;
  onRestore?: () => void;
  /** Queue rows only: leads the menu, above the divider. */
  onOpenInWorkbench?: () => void;
  /**
   * The workbench's Delete claim. Below its own divider and in the error
   * colour, kept apart from the work verbs above: those decide whether a claim
   * shouts tomorrow morning, this one destroys it.
   */
  destructive?: WorkMenuDestructiveAction;
}

/**
 * THE WORK MENU, once, for every surface that offers the verbs (GDN-3380).
 *
 * The queue rows and the workbench kebab offer the same verbs and must look
 * the same doing it. That is a reason to have ONE menu rather than two kept in
 * step: two menus that must agree are two menus that will stop agreeing.
 *
 * The TRIGGER stays with each caller. The queue kebab and the workbench kebab
 * differ in size, placement and accessible name for reasons local to their
 * rows and strips, and folding those into a prop would buy nothing. What is
 * shared is everything below the button: the surface, the item vocabulary, the
 * snooze submenu, the archive dialog and the note composer.
 *
 * It owns the dialog by default. A surface with several archive entry points
 * may supply onRequestArchive and mount the same ArchiveDialog once.
 */
export function WorkMenu({
  anchorEl,
  open,
  onClose,
  subject,
  hidden,
  busy,
  today,
  onArchive,
  onRequestArchive,
  onSnooze,
  onAddNote,
  onRestore,
  onOpenInWorkbench,
  destructive,
}: WorkMenuProps) {
  const [composing, setComposing] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const snoozeAnchor = useRef<HTMLLIElement | null>(null);

  /* A reopened menu starts on its items, not wherever it was left. */
  useEffect(() => {
    if (!open) {
      setComposing(false);
      setSnoozeOpen(false);
    }
  }, [open]);

  const closeAll = useCallback(() => {
    setComposing(false);
    setSnoozeOpen(false);
    onClose();
  }, [onClose]);

  const run = useCallback(
    (action: () => void) => () => {
      closeAll();
      action();
    },
    [closeAll],
  );

  const handleSnooze = useCallback(
    (untilDate: string) => {
      closeAll();
      onSnooze?.({ untilDate });
    },
    [closeAll, onSnooze],
  );

  const handleNote = useCallback(
    (body: string) => {
      closeAll();
      onAddNote?.({ body });
    },
    [closeAll, onAddNote],
  );

  const handleArchive = useCallback(
    (opts: { note?: string }) => {
      setArchiveOpen(false);
      onArchive?.(opts);
    },
    [onArchive],
  );

  const items = [
    onOpenInWorkbench ? (
      <WorkMenuItem
        icon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
        key="open"
        label="Open in workbench"
        onSelect={run(onOpenInWorkbench)}
      />
    ) : null,
    onOpenInWorkbench ? <Divider key="divider-open" /> : null,
    hidden && onRestore ? (
      <WorkMenuItem
        disabled={busy}
        icon={<UnarchiveOutlinedIcon sx={{ fontSize: 18 }} />}
        key="restore"
        label="Restore"
        onSelect={run(onRestore)}
      />
    ) : null,
    !hidden && onArchive && subject ? (
      <WorkMenuItem
        disabled={busy}
        icon={<ArchiveOutlinedIcon sx={{ fontSize: 18 }} />}
        key="archive"
        label="Archive…"
        onSelect={() => {
          setSnoozeOpen(false);
          if (onRequestArchive) onRequestArchive();
          else setArchiveOpen(true);
          onClose();
        }}
      />
    ) : null,
    !hidden && onSnooze ? (
      <WorkMenuItem
        disabled={busy}
        icon={<ScheduleIcon sx={{ fontSize: 18 }} />}
        itemRef={snoozeAnchor}
        key="snooze"
        label="Snooze"
        onSelect={() => setSnoozeOpen(true)}
        trailing={<ChevronRightIcon sx={{ color: 'text.secondary', fontSize: 16 }} />}
      />
    ) : null,
    onAddNote ? (
      <WorkMenuItem
        disabled={busy}
        icon={<NoteAltOutlinedIcon sx={{ fontSize: 18 }} />}
        key="note"
        label="Add note"
        onSelect={() => setComposing(true)}
      />
    ) : null,
  ];
  /* Delete sits below its own divider — when there is something above to divide from. */
  const hasItemsAbove = items.some(item => item !== null);

  return (
    <>
      <Menu
        {...stopPortalEvents}
        anchorEl={anchorEl}
        open={open}
        onClose={closeAll}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        slotProps={{ paper: { sx: WORK_MENU_PAPER_SX } }}
      >
        {composing ? (
          /* The menu's gutter. The composer draws no padding of its own, so
             the same component sits flush inside the workbench Notes card. */
          <Box sx={{ px: 1.5, py: 1 }}>
            <AddNoteComposer
              autoFocus
              busy={busy}
              onCancel={() => setComposing(false)}
              onSave={handleNote}
            />
          </Box>
        ) : (
          [
            ...items,
            destructive && hasItemsAbove ? <Divider key="divider-destructive" /> : null,
            destructive ? (
              <WorkMenuItem
                destructive
                disabled={destructive.busy}
                icon={<DeleteOutlineIcon sx={{ fontSize: 18 }} />}
                key="destructive"
                label={destructive.label}
                onSelect={run(destructive.onSelect)}
              />
            ) : null,
          ]
        )}
      </Menu>

      {onSnooze ? (
        <SnoozeMenu
          anchorEl={snoozeAnchor.current}
          onClose={() => setSnoozeOpen(false)}
          onSelect={handleSnooze}
          open={snoozeOpen}
          today={today}
        />
      ) : null}

      {onArchive && subject && !onRequestArchive ? (
        <ArchiveDialog
          busy={busy}
          onCancel={() => setArchiveOpen(false)}
          onConfirm={handleArchive}
          open={archiveOpen}
          subject={subject}
        />
      ) : null}
    </>
  );
}
